import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import uniqueId from 'lodash/uniqueId';
import isNil from 'lodash/isNil';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  InsuranceAttachmentDTO,
  MultiMediaResponse,
} from 'dtos';
import dayjs from 'utils/dayjs';
import {
  formatFileSize,
} from 'utils/string';
import {
  useGetInsuranceAttachments,
} from 'views/EMR/components/Insurance/hooks';

export type InsuranceAttachmentFormState = {
  existingMediaIds: number[];
  newUploadFiles: FileLocalUrl[];
};

export type ExistingAssociatedInsuranceCard = InsuranceAttachmentDTO & {
  id: string;
  source: 'existing';
  fileName: string;
  metadata: string;
  media: MultiMediaResponse;
};

export type NewAssociatedInsuranceCard = {
  id: string;
  source: 'new';
  fileName: string;
  metadata: string;
  media: MultiMediaResponse;
  original: FileLocalUrl;
};

export type AssociatedInsuranceCard =
  | ExistingAssociatedInsuranceCard
  | NewAssociatedInsuranceCard;

type UseAssociatedInsuranceCardsParams = {
  insuranceId?: number;
  open: boolean;
};

function getCardMetadata(file?: File): string {
  if (isNil(file)) {
    return '--';
  }

  return `${formatFileSize(file.size)} • ${dayjs(file.lastModified).format('MMM D, h:mm A')}`;
}

function toExistingInsuranceCardFromMedia(
  media: MultiMediaResponse,
): ExistingAssociatedInsuranceCard {
  return {
    id: `existing-${media.mediaId ?? uniqueId()}`,
    source: 'existing',
    fileName: media.fileName ?? 'Insurance card',
    metadata: '--',
    media,
  } as ExistingAssociatedInsuranceCard;
}

function toExistingInsuranceCard(
  attachment: InsuranceAttachmentDTO,
): ExistingAssociatedInsuranceCard | null {
  const { media } = attachment;

  if (isNil(media)) {
    return null;
  }

  return {
    ...attachment,
    ...toExistingInsuranceCardFromMedia(media),
    insuranceAttachmentId: attachment.insuranceAttachmentId,
  };
}

function toNewInsuranceCard(file: FileLocalUrl): NewAssociatedInsuranceCard | null {
  const previewUrl = file.dataUrl ?? '';
  const fileName = file.file?.name ?? file.name ?? '';

  if (!fileName || !previewUrl) {
    return null;
  }

  return {
    id: uniqueId('insurance-card__'),
    source: 'new',
    fileName,
    metadata: getCardMetadata(file.file),
    original: file,
    media: {
      fileName,
      url: { value: previewUrl },
      mimeType: file.file?.type,
    } as MultiMediaResponse,
  };
}

export default function useAssociatedInsuranceCards({
  insuranceId,
  open,
}: UseAssociatedInsuranceCardsParams) {
  const {
    data: fetchedAttachments = [],
    isLoading: isLoadingExistingCards,
  } = useGetInsuranceAttachments(open ? insuranceId ?? 0 : 0);
  const [existingCards, setExistingCards] = useState<ExistingAssociatedInsuranceCard[]>([]);
  const [newCards, setNewCards] = useState<NewAssociatedInsuranceCard[]>([]);
  const [shouldDirtyFormState, setShouldDirtyFormState] = useState(false);
  const dialogKeyRef = useRef<string>('');
  const hasInitializedExistingCardsRef = useRef(false);

  const cards = useMemo(
    () => [...existingCards, ...newCards],
    [existingCards, newCards],
  );

  useEffect(() => {
    if (!open) {
      dialogKeyRef.current = '';
      hasInitializedExistingCardsRef.current = false;
      setExistingCards([]);
      setNewCards([]);
      setShouldDirtyFormState(false);
      return;
    }

    const nextDialogKey = `${insuranceId ?? 0}`;

    if (dialogKeyRef.current !== nextDialogKey) {
      dialogKeyRef.current = nextDialogKey;
      hasInitializedExistingCardsRef.current = false;
      setExistingCards([]);
      setNewCards([]);
      setShouldDirtyFormState(false);
    }
  }, [insuranceId, open]);

  const mappedExistingCards = useMemo(
    () => fetchedAttachments
      .map((attachment) => toExistingInsuranceCard(attachment))
      .filter((card): card is ExistingAssociatedInsuranceCard => !isNil(card)),
    [fetchedAttachments],
  );

  useEffect(() => {
    if (!open || hasInitializedExistingCardsRef.current) {
      return;
    }

    if ((insuranceId ?? 0) <= 0) {
      hasInitializedExistingCardsRef.current = true;
      return;
    }

    if (isLoadingExistingCards) {
      return;
    }

    setExistingCards(mappedExistingCards);
    hasInitializedExistingCardsRef.current = true;
  }, [
    insuranceId,
    isLoadingExistingCards,
    mappedExistingCards,
    open,
  ]);

  const appendCards = useCallback((files: FileLocalUrl[]) => {
    const nextCards = files
      .map((file) => toNewInsuranceCard(file))
      .filter((card): card is NewAssociatedInsuranceCard => !isNil(card));

    if (nextCards.length === 0) {
      return;
    }

    setNewCards((currentCards) => [...currentCards, ...nextCards]);
    setShouldDirtyFormState(true);
  }, []);

  const appendExistingCards = useCallback((mediaList: MultiMediaResponse[]) => {
    const currentMediaIds = new Set(
      existingCards
        .map((card) => card.media?.mediaId)
        .filter((mediaId): mediaId is number => !isNil(mediaId) && mediaId > 0),
    );
    const nextCards = mediaList
      .filter((media) => {
        const mediaId = media.mediaId ?? 0;
        const canAppendMedia = mediaId > 0 && !currentMediaIds.has(mediaId);

        if (canAppendMedia) {
          currentMediaIds.add(mediaId);
        }

        return canAppendMedia;
      })
      .map((media) => toExistingInsuranceCardFromMedia(media));

    if (nextCards.length > 0) {
      setExistingCards((currentCards) => [...currentCards, ...nextCards]);
      setShouldDirtyFormState(true);
    }
  }, [existingCards]);

  const removeCard = useCallback((cardId: string) => {
    const cardToRemove = cards.find((card) => card.id === cardId);

    if (isNil(cardToRemove)) {
      return;
    }

    if (cardToRemove.source === 'existing') {
      setExistingCards((currentCards) =>
        currentCards.filter((card) => card.id !== cardId));
    } else {
      setNewCards((currentCards) =>
        currentCards.filter((card) => card.id !== cardId));
    }

    setShouldDirtyFormState(true);
  }, [cards]);

  const existingMediaIds = useMemo(
    () => existingCards
      .map((card) => card.media?.mediaId)
      .filter((mediaId): mediaId is number => !isNil(mediaId) && mediaId > 0),
    [existingCards],
  );

  const newUploadFiles = useMemo(
    () => newCards.map((card) => card.original),
    [newCards],
  );

  return {
    cards,
    appendCards,
    appendExistingCards,
    removeCard,
    isLoadingExistingCards,
    existingMediaIds,
    newUploadFiles,
    shouldDirtyFormState,
  };
}
